import { Capacitor, registerPlugin } from '@capacitor/core'
import { App } from '@capacitor/app'
import { PushNotifications } from '@capacitor/push-notifications'
import storageService from "@/mobile/services/storage";
import router from "@/router";
const FirebaseMessaging = registerPlugin('FirebaseMessaging');

/**
 * Push Notification Handler Module
 * Manages push notification functionality for mobile platforms
 * Handles device registration, permissions, and notification events
 */

// Storage keys constants
const STORAGE_KEYS = {
  DEVICE_TOKEN: "push_notification_device_token",
  REGISTRATION_STATUS: "push_notification_registered",
  LAST_SYNC: "push_notification_last_sync",
  PERMISSION_STATUS: "push_notification_permission_status",
};

// Notification routing configuration
const notificationRoutes = {
  // Voucher notifications
  voucher_claimed: "/client/voucher",
  voucher_cancelled: "/client/voucher",
  voucher_expiring: "/client/voucher",
  point_redeem: "/client/voucher",

  // Member/Profile notifications
  tier_change: "/client/tier",
  tier_danger: "/client/dashboard",
  member_verification: "/client/dashboard",
  user_creation: "/client/dashboard",
  password_reset: "/client/dashboard",
  otp_request: "/client/dashboard",

  // Transaction notifications
  transaction_receipt: "/client/transaction-history",

  // Newsletter notifications
  newsletter: (data) => `/client/newsletters/${data.newsletter_id}`,

  // Default fallback
  default: "/client/dashboard",
};

/**
 * Map known wrong/plural routes from backend FCM payloads to correct Vue Router paths.
 * Backend sometimes sends route with typo (e.g. /client/vouchers instead of /client/voucher).
 */
const ROUTE_NORMALIZATION_MAP = {
  "/client/vouchers": "/client/voucher",
  "/client/tiers": "/client/tier",
  "/client/dashboards": "/client/dashboard",
  "/client/benefits": "/client/benefit",
  "/client/newsletter": "/client/newsletters",
};

/**
 * Normalize a route from FCM payload to the correct Vue Router path.
 * Handles known backend inconsistencies (plural vs singular, etc).
 * @param {string} route - Raw route string from FCM data payload
 * @returns {string} Corrected route path
 */
function normalizeRoute(route) {
  if (!route || typeof route !== "string") return route;
  return ROUTE_NORMALIZATION_MAP[route] || route;
}

// Internal state management
const state = {
  isInitialized: false,
  currentToken: null,
  isRegistered: false,
  permissionStatus: null,
  platform: null,
};

let pendingRegistrationResolver = null;


/**
 * Log push notification errors with structured format
 * Provides consistent error logging across all push notification operations
 * @param {string} context - Context where error occurred (e.g., 'registration', 'unregistration', 'notification_tap')
 * @param {Error} error - The error object
 * @param {Object} additionalData - Additional context data to include in the log
 */
function logPushNotificationError(context, error, additionalData = {}) {
  const errorLog = {
    timestamp: new Date().toISOString(),
    context,
    error: {
      message: error.message || String(error),
      stack: error.stack,
      name: error.name,
    },
    ...additionalData,
  };

  console.error("[PushNotification]", JSON.stringify(errorLog));

  // Optionally send to error tracking service if available
  if (typeof window !== "undefined" && window.errorTracker) {
    try {
      window.errorTracker.captureException(error, {
        tags: { component: "push-notifications", context },
        extra: additionalData,
      });
    } catch (trackerError) {
      // Don't let error tracking failures affect the app
      console.error("[PushNotification] Error tracker failed:", trackerError);
    }
  }
}

/**
 * Extract notification data from payload
 * Handles invalid/malformed payloads with default values
 * @param {Object} notification - Notification payload from FCM
 * @returns {Object} Extracted notification data with defaults
 */
function extractNotificationData(notification) {
  // Default fallback object for invalid/missing data
  const defaultData = {
    type: "unknown",
    title: "Notification",
    body: "",
    additionalData: {},
  };

  try {
    // Validate required fields
    if (!notification || typeof notification !== "object") {
      console.warn(
        "[PushNotification] Invalid notification object:",
        notification,
      );
      return defaultData;
    }

    const data = notification.data || {};
    const notificationInfo = notification.notification || {};

    const title =
      notification.title ||
      notificationInfo.title ||
      data.notification_title ||
      data.title ||
      defaultData.title;

    const body =
      notification.body ||
      notificationInfo.body ||
      data.notification_body ||
      data.body ||
      defaultData.body;

    return {
      type: data.type || "unknown",
      title,
      body,
      additionalData: data,
    };
  } catch (error) {
    logPushNotificationError("extract_notification_data", error, {
      notification,
    });
    // Return safe default - never return null to avoid null checks in callers
    return defaultData;
  }
}

/**
 * Handle tier change notification payload
 * Extracts tier change specific data with comprehensive error handling
 * Returns default values for incomplete/invalid payloads
 * @param {Object} notification - Notification payload from FCM
 * @returns {Object} Extracted tier change data with defaults
 */
function handleTierChangeNotification(notification) {
  // Default fallback object for tier change notifications
  const defaultData = {
    type: "tier_change",
    title: "Tier Changed",
    body: "",
    oldTier: null,
    newTier: "Unknown",
    memberName: "",
    route: "/client/tier",
  };

  try {
    // Validate notification object
    if (!notification || typeof notification !== "object") {
      console.warn(
        "[PushNotification] Invalid tier change notification:",
        notification,
      );
      return defaultData;
    }

    // Extract data and notification info
    const data = notification.data || {};
    const notificationInfo = notification.notification || {};

    const title =
      notification.title ||
      notificationInfo.title ||
      data.notification_title ||
      data.title ||
      defaultData.title;

    const body =
      notification.body ||
      notificationInfo.body ||
      data.notification_body ||
      data.body ||
      defaultData.body;

    // Extract tier change specific fields with fallbacks
    const extractedData = {
      type: "tier_change",
      title,
      body,
      oldTier: data.old_tier !== undefined ? data.old_tier : null,
      newTier: data.new_tier || defaultData.newTier,
      memberName: data.member_name || defaultData.memberName,
      route: data.route || defaultData.route,
    };

    return extractedData;
  } catch (error) {
    logPushNotificationError("handle_tier_change_notification", error, {
      notification,
    });
    // Return safe default - never return null to avoid null checks in callers
    return defaultData;
  }
}

/**
 * Handle newsletter notification payload
 * Extracts newsletter specific data with comprehensive error handling
 * Returns default values for incomplete/invalid payloads
 * @param {Object} notification - Notification payload from FCM
 * @returns {Object} Extracted newsletter data with defaults
 */
function handleNewsletterNotification(notification) {
  // Default fallback object for newsletter notifications
  const defaultData = {
    type: "newsletter",
    title: "Newsletter Baru",
    body: "",
    newsletterId: null,
    newsletterTitle: "",
    newsletterExcerpt: "",
    route: "/client/newsletters",
  };

  try {
    // Validate notification object
    if (!notification || typeof notification !== "object") {
      console.warn(
        "[PushNotification] Invalid newsletter notification:",
        notification,
      );
      return defaultData;
    }

    // Extract data and notification info
    const data = notification.data || {};
    const notificationInfo = notification.notification || {};

    const title =
      notification.title ||
      notificationInfo.title ||
      data.notification_title ||
      data.title ||
      defaultData.title;

    const body =
      notification.body ||
      notificationInfo.body ||
      data.notification_body ||
      data.body ||
      defaultData.body;

    // Extract newsletter specific fields with fallbacks
    const extractedData = {
      type: "newsletter",
      title,
      body,
      newsletterId: data.newsletter_id || null,
      newsletterTitle: data.newsletter_title || data.title || "",
      newsletterExcerpt:
        data.newsletter_excerpt || data.body || "",
      route: data.route || defaultData.route,
    };

    return extractedData;
  } catch (error) {
    logPushNotificationError("handle_newsletter_notification", error, {
      notification,
    });
    // Return safe default - never return null to avoid null checks in callers
    return defaultData;
  }
}

/**
 * Get route for notification type
 * Returns the appropriate route path for a given notification type
 * Handles both static routes and dynamic route functions
 * Falls back to home route for unknown types
 * @param {string} notificationType - Type of notification
 * @param {Object} data - Notification data for dynamic routes
 * @returns {string} Route path
 */
function getRouteForNotification(notificationType, data = {}) {
  const route = notificationRoutes[notificationType];

  // Handle function routes (dynamic routes)
  if (typeof route === "function") {
    return route(data);
  }

  // Handle string routes
  return route || notificationRoutes.default;
}

/**
 * Navigate to tier page from tier change notification
 * Extracts route from notification payload and handles navigation errors
 * Falls back to home ("/") if navigation fails
 * @param {Object} payload - Tier change notification payload
 * @returns {Promise<void>}
 */
async function navigateToTierPage(payload) {
  try {
    // Extract route from notification payload
    const route = payload.data?.route || "/client/tier";

    // Attempt navigation to tier page
    await router.push(route);
  } catch (error) {
    // Log navigation error for debugging
    logPushNotificationError("navigate_to_tier_page", error, {
      payload,
      attemptedRoute: payload.data?.route || "/client/tier",
    });

    // Fallback navigation to home on error
    try {
      await router.push("/");
    } catch (fallbackError) {
      // Log fallback error but don't throw - allow app to continue
      logPushNotificationError(
        "navigate_to_tier_page_fallback",
        fallbackError,
        {
          payload,
        },
      );
      console.error(
        "[PushNotification] Both tier page and fallback navigation failed",
      );
    }
  }
}

/**
 * Navigate to newsletter page from newsletter notification
 * Extracts newsletter_id from notification payload and handles navigation errors
 * Validates tenant context before navigation
 * Falls back to newsletter list if newsletter_id missing or navigation fails
 * @param {Object} payload - Newsletter notification payload
 * @returns {Promise<void>}
 */
async function navigateToNewsletterPage(payload) {
  try {
    // Extract newsletter_id from notification payload
    const newsletterId = payload.data?.newsletter_id;

    if (!newsletterId) {
      console.warn(
        "[PushNotification] Newsletter ID missing, navigating to newsletter list",
      );

      // Track security event for missing newsletter ID
      trackSecurityEvent("newsletter_notification_missing_id", {
        payload: payload.data,
      });

      await router.push("/client/newsletters");
      return;
    }

    // Validate tenant context if available in payload
    if (payload.data?.tenant_domain) {
      const isValidTenant = await validateNotificationTenantContext(
        payload.data.tenant_domain,
      );

      if (!isValidTenant) {
        console.warn(
          "[PushNotification] Tenant mismatch in newsletter notification",
        );

        trackSecurityEvent("newsletter_notification_tenant_mismatch", {
          newsletterId,
          notificationTenant: payload.data.tenant_domain,
        });

        // Navigate to newsletter list instead of detail
        await router.push("/client/newsletters");
        return;
      }
    }

    const route = `/client/newsletters/${newsletterId}`;

    await router.push(route);
  } catch (error) {
    // Log navigation error for debugging
    console.error("[PushNotification] ❌ Newsletter navigation error:", error);
    console.error("[PushNotification] Error stack:", error.stack);
    logPushNotificationError("navigate_to_newsletter_page", error, { payload });

    // Fallback to newsletter list
    try {
      await router.push("/client/newsletters");
    } catch (fallbackError) {
      logPushNotificationError(
        "navigate_to_newsletter_page_fallback",
        fallbackError,
        { payload },
      );
      // Final fallback to home
      await router.push("/");
    }
  }
}

/**
 * Validate tenant context for notification
 * Checks if notification tenant matches current user tenant
 * @param {string} notificationTenant - Tenant domain from notification
 * @returns {Promise<boolean>} True if tenant is valid
 */
async function validateNotificationTenantContext(notificationTenant) {
  try {
    // Import store dynamically to avoid circular dependencies
    const store = (await import("@/store")).default;

    const currentUser =
      store.state.Auth?.currUser || store.state.Auth?.userInfo;
    const userTenant = currentUser?.member?.corporate_domain;

    if (!userTenant) {
      console.warn(
        "[PushNotification] User tenant not available, allowing access",
      );
      return true; // Allow if user tenant not available (backend will enforce)
    }

    const isValid = userTenant === notificationTenant;

    if (!isValid) {
      console.warn("[PushNotification] Tenant validation failed", {
        userTenant,
        notificationTenant,
      });
    }

    return isValid;
  } catch (error) {
    console.error("[PushNotification] Tenant validation error:", error);
    return true; // Allow on error (backend will enforce)
  }
}

/**
 * Track security events for monitoring and audit
 * @param {string} eventType - Type of security event
 * @param {Object} details - Additional event details
 */
function trackSecurityEvent(eventType, details = {}) {
  try {
    const securityEvent = {
      timestamp: new Date().toISOString(),
      eventType,
      source: "push_notification",
      ...details,
    };

    console.warn(
      "[PushNotification Security Event]",
      JSON.stringify(securityEvent),
    );

    // Dispatch to window for external monitoring tools
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("security-event", {
          detail: securityEvent,
        }),
      );
    }

    // TODO: Send to backend analytics/security logging endpoint
    // const api = (await import("@/helpers/api")).default;
    // api.request('post', '/api/client/security-events', {}, securityEvent);
  } catch (error) {
    console.error("[PushNotification] Failed to track security event:", error);
  }
}

/**
 * Navigate to appropriate route based on notification payload
 * Handles navigation errors gracefully with fallback to home
 * @param {Object} payload - Notification payload
 * @returns {Promise<void>}
 */
async function navigateFromNotification(payload) {
  try {
    // 1. Pastikan auth session terhidrasi dari native storage tanpa delay/freeze 3 detik
    try {
      const store = (await import("@/store")).default;
      if (!store.state.Auth.isHydrated) {
        await store.dispatch("Auth/restoreSession");
      }
    } catch (storeError) {
      console.error("[PushNotification] Error restoring auth session:", storeError);
    }

    // 2. Pastikan inisialisasi rute awal router sudah selesai
    await router.isReady();

    // Extract notification type from payload
    const notificationType = payload.data?.type;
    const rawDirectRoute = payload.data?.route;

    // Normalize directRoute to fix known backend typos (e.g. /client/vouchers → /client/voucher)
    const directRoute = normalizeRoute(rawDirectRoute);

    if (rawDirectRoute && rawDirectRoute !== directRoute) {
      console.warn(
        `[PushNotification] Route from payload "${rawDirectRoute}" was normalized to "${directRoute}"`
      );
    }

    // Only use directRoute if there is no type-based route defined.
    // Type-based routing (notificationRoutes) is the source of truth — backend route is a hint only.
    const typeBasedRoute = notificationRoutes[notificationType];
    const shouldUseDirectRoute =
      directRoute &&
      typeof directRoute === "string" &&
      directRoute.length > 0 &&
      !typeBasedRoute; // only use direct if no type mapping exists

    if (shouldUseDirectRoute) {
      try {
        await router.push(directRoute);
        return;
      } catch (directRouteError) {
        console.warn("[PushNotification] Direct route failed, trying type-based routing:", directRouteError);
        // Fall through to type-based routing
      }
    }

    // Handle tier_change notifications with dedicated function
    if (notificationType === "tier_change") {
      await navigateToTierPage(payload);
      return;
    }
    
    // Handle newsletter notifications with dedicated function
    if (notificationType === "newsletter") {
      await navigateToNewsletterPage(payload);
      return;
    }

    // Get route for notification type
    const route = getRouteForNotification(notificationType, payload.data || {});

    if (!route) {
      console.warn(
        "[PushNotification] No route defined for notification type:",
        notificationType,
      );
      // Navigate to dashboard as fallback
      await router.push("/client/dashboard");
      return;
    }

    // Build route object with query params for additional data
    const routeObject = { path: route };

    // For voucher-related notifications, add query params to auto-open modal
    if (
      (notificationType === "voucher_claimed" || 
       notificationType === "voucher_cancelled" ||
       notificationType === "point_redeem" ||
       notificationType === "transaction_receipt") &&
      payload.data?.transaction_code
    ) {
      routeObject.query = {
        transaction_code: payload.data.transaction_code,
        auto_open: "true", // Flag to auto-open modal
      };
    }

    await router.push(routeObject);
  } catch (error) {
    logPushNotificationError("navigation", error, {
      payload,
      notificationType: payload?.data?.type,
    });

    // Try to navigate to dashboard as last resort
    try {
      await router.push("/client/dashboard");
    } catch (fallbackError) {
      console.error(
        "[PushNotification] Fallback navigation also failed:",
        fallbackError,
      );
      logPushNotificationError("navigation_fallback", fallbackError, {
        payload,
      });
      // Don't throw - just log the error
    }
  }
}

/**
 * Handle notification received in foreground.
 * The native iOS platform displays the system notification banner
 * via willPresentNotification (presentationOptions).
 * @param {Object} notification - Notification payload from FCM
 */
async function onNotificationReceived(notification) {
  try {
    // Extract notification data based on type
    const notificationType = notification.data?.type;
    let notificationData;

    if (notificationType === "newsletter") {
      notificationData = handleNewsletterNotification(notification);
    } else if (notificationType === "tier_change") {
      notificationData = handleTierChangeNotification(notification);
    } else {
      notificationData = extractNotificationData(notification);
    }

    console.log(
      "[PushNotification] Foreground notification received (handled natively by presentationOptions):",
      notificationData,
    );

  } catch (error) {
    console.error(
      "[PushNotification] Failed to process foreground notification:",
      error,
    );
    logPushNotificationError("notification_received", error, { notification });
  }
}

/**
 * Handle notification received in background
 * Called when notification arrives while app is in background or killed state
 * @param {Object} notification - Notification payload from FCM
 */
async function onBackgroundNotificationReceived(notification) {
  try {
    // Extract notification data
    const notificationData = extractNotificationData(notification);

    // Store notification for later processing if needed
    try {
      const pendingNotifications =
        (await storageService.get("pending_notifications")) || [];
      pendingNotifications.push({
        ...notificationData,
        receivedAt: Date.now(),
        state: "background",
      });
      await storageService.set("pending_notifications", pendingNotifications);
    } catch (storageError) {
      logPushNotificationError(
        "background_notification_storage",
        storageError,
        {
          notificationData,
        },
      );
    }
  } catch (error) {
    logPushNotificationError("background_notification_received", error, {
      notification,
    });
  }
}

/**
 * Handle notification tap event
 * Called when user taps on a notification
 * Extracts notification type and navigates to appropriate route
 * @param {Object} notification - Notification action payload from Capacitor
 */
async function onNotificationTapped(notification) {
  try {
    console.log("[PushNotification] 📱 Notification tapped:", notification);
    
    // Try multiple ways to access data
    const data1 = notification.data;
    const data2 = notification.notification?.data;
    const data3 = notification.notification?.notification?.data;

    // Use the first non-null data
    const actualData = data1 || data2 || data3 || {};
    
    console.log("[PushNotification] Extracted data:", actualData);

    // Create a normalized notification object
    const normalizedNotification = {
      ...notification,
      data: actualData,
    };

    // Extract notification data (for logging/side effects if needed, but not used here)
    extractNotificationData(normalizedNotification);

    // Navigate to appropriate route based on notification type
    await navigateFromNotification(normalizedNotification);
    
    // Remove only the tapped notification from the tray, leaving other notifications intact.
    // The notification object lives at notification.notification (Capacitor ActionPerformed payload).
    const notifObj = notification.notification || notification;
    const tappedId = notifObj?.id ?? null;
    const tappedTag = notifObj?.tag ?? null;
    const canRemoveTappedNotification = getPlatform() !== "android";
    if (tappedId && isNativePlatform() && canRemoveTappedNotification) {
      try {
        const notifToRemove = { id: String(tappedId) };
        if (tappedTag) {
          notifToRemove.tag = tappedTag;
        }
        await PushNotifications.removeDeliveredNotifications({
          notifications: [notifToRemove],
        });
        await clearBadgeCount();
      } catch (removeErr) {
        console.warn("[PushNotification] Could not remove single delivered notification:", removeErr);
      }
    }

    console.log("[PushNotification] Navigation completed successfully");
  } catch (error) {
    console.error(
      "[PushNotification] CRITICAL ERROR in onNotificationTapped:",
      error,
    );
    console.error("[PushNotification] Error stack:", error.stack);
    logPushNotificationError("notification_tap", error, { notification });
    // Emergency fallback - try to at least show something
    try {
      await router.push("/client/dashboard");
    } catch (fallbackError) {
      console.error("[PushNotification] Even fallback failed:", fallbackError);
    }
  }
}

/**
 * Create notification channels for Android
 * Required for Android 8.0+ to display notifications properly
 */
async function createNotificationChannels() {
  if (getPlatform() !== "android") {
    return;
  }

  try {
    // Delete existing channels first so updated importance/sound settings take effect on device
    try {
      await PushNotifications.deleteChannel({ id: "default" });
      await PushNotifications.deleteChannel({ id: "high_priority" });
      await PushNotifications.deleteChannel({ id: "voucher" });
    } catch (e) {
      // Ignore if channels do not exist yet
    }

    // Create default channel (omit custom sound to use system default notification sound)
    await PushNotifications.createChannel({
      id: "default",
      name: "Default Notifications",
      description: "General notifications",
      importance: 5, // Max importance for heads-up alert
      visibility: 1, // Public
      vibration: true,
      lights: true,
      lightColor: "#FF0000",
    });

    // Create high priority channel for important notifications
    await PushNotifications.createChannel({
      id: "high_priority",
      name: "Important Notifications",
      description:
        "High priority notifications that require immediate attention",
      importance: 5, // Max importance
      visibility: 1, // Public
      vibration: true,
      lights: true,
      lightColor: "#FF0000",
    });

    // Create voucher channel
    await PushNotifications.createChannel({
      id: "voucher",
      name: "Voucher Notifications",
      description: "Notifications about vouchers and rewards",
      importance: 5,
      visibility: 1,
      vibration: true,
    });

    await PushNotifications.createChannel({
      id: "general",
      name: "General Notifications",
      description: "Points, transactions, newsletters and account notifications",
      importance: 5,
      visibility: 1,
      vibration: true,
    });
  } catch (error) {
    logPushNotificationError("create_notification_channels", error);
  }
}

/**
 * Set up event listeners for push notifications
 * This must be called BEFORE PushNotifications.register()
 */
function setupEventListeners() {
  try {
    // Listen for successful registration and token receipt
    PushNotifications.addListener("registration", async (token) => {
      try {
        // Get platform
        const platform = getPlatform();

        // Resolve token to FCM token
        let effectiveToken = token.value;
        console.log("[PushNotification] Raw APNs device token:", token.value);
        if (platform === "ios") {
          try {
            const fcmResult = await FirebaseMessaging.getToken();
            if (fcmResult && fcmResult.token) {
              console.log("[PushNotification] Successfully resolved iOS FCM token:", fcmResult.token.substring(0, 15) + "...");
              effectiveToken = fcmResult.token;
            }
          } catch (fcmError) {
            console.warn("[PushNotification] Could not get FCM token on iOS, fallback to APNs token:", fcmError);
          }
        }

        // Check if this is a token refresh (we already have a stored token)
        const storedToken = await storageService.get(STORAGE_KEYS.DEVICE_TOKEN);
        const isTokenRefresh = storedToken && storedToken !== effectiveToken;

        // Send token to backend API
        const api = (await import("@/helpers/api")).default;

        // Prepare request data
        let requestData;
        let endpoint;

        if (isTokenRefresh) {
          // Use refresh endpoint for token refresh
          endpoint = "/api/client/device/refresh";
          requestData = {
            old_token: storedToken,
            new_token: effectiveToken,
            device_type: platform,
          };
        } else {
          // Use register endpoint for new registration
          endpoint = "/api/client/device";
          requestData = {
            device_token: effectiveToken,
            device_type: platform,
          };
        }

        try {
          const method = isTokenRefresh ? "put" : "post";
          await api.request(method, endpoint, {}, requestData);

          // Store registration status locally
          await storageService.set(STORAGE_KEYS.DEVICE_TOKEN, effectiveToken);
          await storageService.set(STORAGE_KEYS.REGISTRATION_STATUS, true);
          await storageService.set(STORAGE_KEYS.LAST_SYNC, Date.now());

          // Update internal state
          state.currentToken = effectiveToken;
          state.isRegistered = true;
        } catch (apiError) {
          logPushNotificationError("backend_registration", apiError, {
            token: effectiveToken,
            platform,
            isTokenRefresh,
          });

          // Handle specific error cases
          if (apiError.response?.status === 409) {
            await storageService.set(STORAGE_KEYS.DEVICE_TOKEN, effectiveToken);
            await storageService.set(STORAGE_KEYS.REGISTRATION_STATUS, true);
            state.currentToken = effectiveToken;
            state.isRegistered = true;
          } else {
            // Store failure state but don't throw
            await storageService.set(STORAGE_KEYS.REGISTRATION_STATUS, false);
          }
        }
      } catch (error) {
        logPushNotificationError("registration_listener", error, {
          token: token.value,
        });
      } finally {
        if (typeof pendingRegistrationResolver === "function") {
          pendingRegistrationResolver(state.isRegistered);
          pendingRegistrationResolver = null;
        }
      }
    });

    // Listen for registration errors
    PushNotifications.addListener("registrationError", async (error) => {
      console.error("[PushNotification] FCM registration error:", error);
      logPushNotificationError("fcm_registration_error", error);
      await storageService.set(STORAGE_KEYS.REGISTRATION_STATUS, false);
      if (typeof pendingRegistrationResolver === "function") {
        pendingRegistrationResolver(false);
        pendingRegistrationResolver = null;
      }
    });

    // Listen for notification received in foreground
    PushNotifications.addListener(
      "pushNotificationReceived",
      onNotificationReceived,
    );

    // Listen for notification tap (when user taps on a notification)
    // This works for both foreground, background, and killed state
    PushNotifications.addListener(
      "pushNotificationActionPerformed",
      onNotificationTapped,
    );

    // Clear badge count whenever app returns to foreground
    App.addListener("appStateChange", async ({ isActive }) => {
      if (isActive) {
        await clearBadgeCount();
      }
    });

  } catch (error) {
    logPushNotificationError("setup_event_listeners", error);
    // Don't throw - allow initialization to continue
  }
}

/**
 * Clear app icon badge and delivered notifications
 */
async function clearBadgeCount() {
  if (!isNativePlatform()) return;
  try {
    await PushNotifications.removeAllDeliveredNotifications();

    // Reset backend iOS badge counter if on iOS and authenticated
    if (getPlatform() === "ios") {
      const store = (await import("@/store")).default;
      if (store?.getters["Auth/isLoggedIn"]) {
        const storedToken = await storageService.get(STORAGE_KEYS.DEVICE_TOKEN);
        if (storedToken) {
          const api = (await import("@/helpers/api")).default;
          api.post("/api/client/device/reset-badge", { device_token: storedToken }).catch(() => { });
        }
      }
    }
  } catch (err) {
    console.warn("[PushNotification] Failed to clear badge count:", err);
  }
}

/**
 * Check if running on native mobile platform
 * @returns {boolean} True if on native platform (Android/iOS)
 */
function isNativePlatform() {
  return Capacitor.isNativePlatform();
}

/**
 * Get current platform
 * @returns {string} Platform name ('android', 'ios', or 'web')
 */
function getPlatform() {
  return Capacitor.getPlatform();
}

/**
 * Initialize the push notification system
 * Sets up event listeners and checks initial permission state
 * Only activates on native mobile platforms
 * @returns {Promise<void>}
 */
async function initialize() {
  // Only initialize on native platforms
  if (!isNativePlatform()) {
    return;
  }

  if (state.isInitialized) {
    return;
  }

  try {
    state.platform = getPlatform();

    // Create notification channels (Android only)
    await createNotificationChannels();

    // Set up event listeners
    setupEventListeners();

    // Clear any residual badges on startup
    await clearBadgeCount();

    // Load stored state
    try {
      state.currentToken = await storageService.get(STORAGE_KEYS.DEVICE_TOKEN);
      state.isRegistered =
        (await storageService.get(STORAGE_KEYS.REGISTRATION_STATUS)) || false;
      state.permissionStatus = await storageService.get(
        STORAGE_KEYS.PERMISSION_STATUS,
      );
    } catch (storageError) {
      logPushNotificationError("initialize_load_state", storageError);
      // Continue initialization even if storage fails
    }

    state.isInitialized = true;
  } catch (error) {
    logPushNotificationError("initialize", error, { platform: state.platform });
    // Don't throw - allow app to continue
  }
}

/**
 * Register device for push notifications
 * Called after successful login
 * @returns {Promise<void>}
 */
async function registerDevice() {
  if (!isNativePlatform()) {
    return;
  }

  try {
    // Check current permission status
    let permissionStatus;
    try {
      permissionStatus = await PushNotifications.checkPermissions();
    } catch (permError) {
      logPushNotificationError("check_permissions", permError);
      // Assume denied if check fails
      await storageService.set(STORAGE_KEYS.PERMISSION_STATUS, "denied");
      return;
    }

    // Request permissions if not granted
    if (permissionStatus.receive !== "granted") {
      try {
        const requestResult = await PushNotifications.requestPermissions();

        if (requestResult.receive !== "granted") {
          await storageService.set(STORAGE_KEYS.PERMISSION_STATUS, "denied");
          return;
        }

        await storageService.set(STORAGE_KEYS.PERMISSION_STATUS, "granted");
      } catch (requestError) {
        logPushNotificationError("request_permissions", requestError);
        await storageService.set(STORAGE_KEYS.PERMISSION_STATUS, "denied");
        return;
      }
    }

    // Create a promise to wait for token delivery and backend registration
    const registrationPromise = new Promise((resolve) => {
      pendingRegistrationResolver = resolve;
      // Safety timeout: 5s max so login is never blocked if network is slow/offline
      setTimeout(() => {
        if (pendingRegistrationResolver === resolve) {
          pendingRegistrationResolver = null;
          resolve(false);
        }
      }, 5000);
    });

    // Register with FCM to get device token
    try {
      await PushNotifications.register();
    } catch (registerError) {
      logPushNotificationError("fcm_register", registerError);
      await storageService.set(STORAGE_KEYS.REGISTRATION_STATUS, false);
      if (typeof pendingRegistrationResolver === "function") {
        pendingRegistrationResolver(false);
        pendingRegistrationResolver = null;
      }
      return;
    }

    // Await token delivery and backend registration before proceeding
    await registrationPromise;
  } catch (error) {
    logPushNotificationError("register_device", error);
    await storageService.set(STORAGE_KEYS.REGISTRATION_STATUS, false);
    if (typeof pendingRegistrationResolver === "function") {
      pendingRegistrationResolver(false);
      pendingRegistrationResolver = null;
    }
    // Don't throw - allow login flow to continue
  }
}

/**
 * Unregister device from push notifications
 * Called during logout
 * @returns {Promise<void>}
 */
async function unregisterDevice() {
  if (!isNativePlatform()) {
    return;
  }

  try {
    // Retrieve stored device token
    let storedToken;
    try {
      storedToken = await storageService.get(STORAGE_KEYS.DEVICE_TOKEN);
    } catch (storageError) {
      logPushNotificationError("unregister_get_token", storageError);
      // Continue with cleanup even if we can't get the token
      storedToken = null;
    }

    if (!storedToken) {
      // Clear any stale registration status
      try {
        await storageService.remove(STORAGE_KEYS.REGISTRATION_STATUS);
        await storageService.remove(STORAGE_KEYS.LAST_SYNC);
      } catch (cleanupError) {
        logPushNotificationError("unregister_cleanup_no_token", cleanupError);
      }
      state.isRegistered = false;
      return;
    }

    // Send token to backend API for deletion
    try {
      const api = (await import("@/helpers/api")).default;

      await api.request(
        "delete",
        "/api/client/device",
        {},
        {
          device_token: storedToken, // ✅ Changed from 'token' to 'device_token'
        },
      );
    } catch (apiError) {
      // Handle specific expected error cases (session already terminated or device not found)
      if (apiError.response?.status === 401) {
        console.warn("[PushNotification] User already unauthenticated, skipping backend device unregistration");
      } else if (apiError.response?.status === 404) {
        console.warn("[PushNotification] Device token already unregistered or not found");
      } else {
        logPushNotificationError("backend_unregistration", apiError, {
          token: storedToken,
        });
      }
    }

    // Clear local storage regardless of backend result
    try {
      await storageService.remove(STORAGE_KEYS.DEVICE_TOKEN);
      await storageService.remove(STORAGE_KEYS.REGISTRATION_STATUS);
      await storageService.remove(STORAGE_KEYS.LAST_SYNC);

      // Update internal state
      state.currentToken = null;
      state.isRegistered = false;
    } catch (storageError) {
      logPushNotificationError("unregister_clear_storage", storageError);
      // Still update state even if storage fails
      state.currentToken = null;
      state.isRegistered = false;
    }
  } catch (error) {
    logPushNotificationError("unregister_device", error);

    // Even on error, try to clear local storage to prevent stale data
    try {
      await storageService.remove(STORAGE_KEYS.DEVICE_TOKEN);
      await storageService.remove(STORAGE_KEYS.REGISTRATION_STATUS);
      await storageService.remove(STORAGE_KEYS.LAST_SYNC);
      state.currentToken = null;
      state.isRegistered = false;
    } catch (cleanupError) {
      logPushNotificationError("unregister_final_cleanup", cleanupError);
    }

    // Don't throw - allow logout flow to continue
  }
}

/**
 * Check current permission status
 * @returns {Promise<PermissionStatus>} Current permission state
 */
async function checkPermissionStatus() {
  if (!isNativePlatform()) {
    return { receive: "denied" };
  }

  try {
    // Call PushNotifications.checkPermissions()
    const permissionStatus = await PushNotifications.checkPermissions();

    // Update internal state
    state.permissionStatus = permissionStatus.receive;

    // Store permission status
    try {
      await storageService.set(
        STORAGE_KEYS.PERMISSION_STATUS,
        permissionStatus.receive,
      );
    } catch (storageError) {
      logPushNotificationError("check_permission_storage", storageError);
      // Continue even if storage fails
    }

    return permissionStatus;
  } catch (error) {
    logPushNotificationError("check_permission_status", error);
    // Return denied on error
    return { receive: "denied" };
  }
}

/**
 * Request notification permissions from user
 * @returns {Promise<PermissionStatus>} Granted permission state
 */
async function requestPermissions() {
  if (!isNativePlatform()) {
    return { receive: "denied" };
  }

  try {
    // Call PushNotifications.requestPermissions()
    const result = await PushNotifications.requestPermissions();

    // Handle user response
    if (result.receive === "denied") {
      // Update internal state
      state.permissionStatus = "denied";

      // Store permission status
      try {
        await storageService.set(STORAGE_KEYS.PERMISSION_STATUS, "denied");
      } catch (storageError) {
        logPushNotificationError(
          "request_permission_storage_denied",
          storageError,
        );
      }

      // Return denied status - this is a valid user choice, not an error
      return { receive: "denied" };
    }

    // Permission granted

    // Update internal state
    state.permissionStatus = result.receive;

    // Store permission status
    try {
      await storageService.set(STORAGE_KEYS.PERMISSION_STATUS, result.receive);
    } catch (storageError) {
      logPushNotificationError(
        "request_permission_storage_granted",
        storageError,
      );
      // Continue even if storage fails
    }

    return result;
  } catch (error) {
    logPushNotificationError("request_permissions", error);

    // Assume denied if error occurs
    state.permissionStatus = "denied";
    try {
      await storageService.set(STORAGE_KEYS.PERMISSION_STATUS, "denied");
    } catch (storageError) {
      logPushNotificationError(
        "request_permission_storage_error",
        storageError,
      );
    }

    return { receive: "denied" };
  }
}

// Public API
export const pushNotificationHandler = {
  initialize,
  registerDevice,
  unregisterDevice,
  checkPermissionStatus,
  requestPermissions,
};

// Export storage keys for testing
export { STORAGE_KEYS };

// Export helper functions for testing
export {
  extractNotificationData,
  handleTierChangeNotification,
  handleNewsletterNotification,
  onNotificationReceived,
  onBackgroundNotificationReceived,
  onNotificationTapped,
  getRouteForNotification,
  navigateFromNotification,
  navigateToTierPage,
  navigateToNewsletterPage,
  validateNotificationTenantContext,
  trackSecurityEvent,
  logPushNotificationError,
  createNotificationChannels,
  clearBadgeCount,
};

// Export state for testing (read-only access)
export function getState() {
  return { ...state }
}
